import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE_URL } from '../../config/api';
import { trackEvents } from '../../utils/analytics';
import { chatGameApi } from './chatGameApi';
import ReportFlag from './ReportFlag';

/**
 * LivestreamPanel — WebRTC livestreaming via LiveKit
 * Allows users to broadcast webcam/screen and watch others' streams.
 */

let LiveKitComponents = null;
let liveKitLoaded = false;
let liveKitLoadPromise = null;

// Lazy-load LiveKit (it's a large dependency)
function loadLiveKit() {
  if (liveKitLoaded) return Promise.resolve();
  if (liveKitLoadPromise) return liveKitLoadPromise;
  liveKitLoadPromise = Promise.all([
    import('@livekit/components-react'),
    import('@livekit/components-styles'),
    import('livekit-client'),
  ]).then(([components, , client]) => {
    LiveKitComponents = { ...components, Track: client.Track, RoomEvent: client.RoomEvent };
    liveKitLoaded = true;
  });
  return liveKitLoadPromise;
}

// Stable per-browser identity for viewers without a seat (playerId null).
// Without this every anonymous viewer joined LiveKit as 'Spectator' and each
// new join evicted the previous one (LiveKit disconnects duplicate identities).
function getBrowserViewerId() {
  try {
    let id = localStorage.getItem('lk_viewer_id');
    if (!id) {
      id = 'viewer-' + Math.random().toString(36).slice(2, 10);
      localStorage.setItem('lk_viewer_id', id);
    }
    return id;
  } catch (e) {
    return 'viewer-' + Math.random().toString(36).slice(2, 10);
  }
}

const MAX_AUTO_RECONNECT = 5;

// getDisplayMedia does not exist on iOS Safari / most mobile browsers. Every
// Share Screen entry point gates on this so an unsupported device never buys
// a metered LiveKit connect for a capture mode that can only throw.
const CAN_SCREEN_SHARE = typeof navigator !== 'undefined'
  && !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);

// Coarse-pointer device (same heuristic as the MiniPlayer hover reveal) —
// drives the touch-target floors; a device class, so computed once.
const IS_TOUCH = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && !window.matchMedia('(hover: hover)').matches;

const USER_LIST_COLORS = ['#7C4DFF', '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#8b5cf6'];
const getUserColor = (name) => USER_LIST_COLORS[(name || '').charCodeAt(0) % USER_LIST_COLORS.length];

/**
 * SpaceUserList — the "👥 N in this space" strip + user rows with LIVE badges
 * and ▶ Watch buttons. Rendered by BOTH the idle (disconnected) view and the
 * connected room view, so the panel always shows the room is alive. Data
 * sources differ (LiveKit tracks when connected, the status-poll announce
 * side channel when idle) — callers pass the resolved names/identities.
 */
const SpaceUserList = ({ allUsers, moderatorCharacter, liveUserNames, liveIdentityByName, watchedIdentity = null, onWatch, textColor, mutedColor, defaultExpanded = true }) => {
  // Idle view opens the list by default (the room should look alive before
  // anyone connects); the connected room view starts it collapsed so the
  // stage + reactions stay the focus once you're live/watching.
  const [usersExpanded, setUsersExpanded] = useState(defaultExpanded);
  const COLLAPSED_USER_COUNT = 1;
  const totalUserCount = allUsers.length + (moderatorCharacter ? 1 : 0);
  const hasHiddenUsers = totalUserCount > COLLAPSED_USER_COUNT;
  // Live streamers first (Nina 07-12) — the list should lead with who you can
  // actually join. Stable sort keeps the original order otherwise.
  const orderedUsers = [...allUsers].sort((a, b) =>
    (liveUserNames.has(b.name) ? 1 : 0) - (liveUserNames.has(a.name) ? 1 : 0));

  return (
    <>
      {/* Slim user toggle strip — always present so the layout is identical
          whether or not anyone is live (stage first, people on demand) */}
      <button
        onClick={() => setUsersExpanded(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          width: '100%',
          padding: '5px 16px',
          background: 'rgba(0,0,0,0.25)',
          border: 'none',
          color: mutedColor,
          fontSize: '0.7em',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          cursor: 'pointer',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        👥 {totalUserCount} in this space
        <span style={{ marginLeft: 'auto', display: 'inline-block', transform: usersExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▾</span>
      </button>

      {/* Vertical user list — collapsed behind the strip until toggled.
          Touch devices skip the inner 110px scroller: a nested scroll region
          inside the full-screen TV place scroller was a scroll-capture trap
          right where the ▶ Watch picker lives — the outer scroller owns it. */}
      <div style={{
        flex: '0 0 auto',
        maxHeight: IS_TOUCH ? 'none' : '110px',
        overflowY: IS_TOUCH ? 'visible' : 'auto',
        padding: '0 16px 8px',
        zIndex: 1,
        display: usersExpanded ? 'block' : 'none',
      }}>
        {/* Moderator */}
        {moderatorCharacter && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '5px 8px',
            borderRadius: '8px',
            marginBottom: '2px',
            background: 'linear-gradient(135deg, rgba(218,165,32,0.1), transparent)',
          }}>
            {moderatorCharacter.profile_image_url ? (
              <img src={moderatorCharacter.profile_image_url} alt="" style={{
                width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover',
                border: '2px solid #DAA520',
                boxShadow: '0 0 6px rgba(218,165,32,0.4)',
                flexShrink: 0,
              }} />
            ) : (
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #DAA520, #B8860B)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65em', flexShrink: 0,
                boxShadow: '0 0 6px rgba(218,165,32,0.4)',
              }}>
                {moderatorCharacter.avatar_emoji || '🎙️'}
              </div>
            )}
            <span style={{
              color: '#DAA520',
              fontSize: '0.78em',
              fontWeight: 700,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {moderatorCharacter.name}
            </span>
            <span style={{
              padding: '1px 5px',
              borderRadius: '3px',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: '0.55em',
              fontWeight: 800,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              animation: 'liveBadgePulse 2s ease-in-out infinite',
              flexShrink: 0,
            }}>
              LIVE
            </span>
            <span style={{
              padding: '1px 5px',
              borderRadius: '3px',
              backgroundColor: 'rgba(218,165,32,0.25)',
              color: '#DAA520',
              fontSize: '0.55em',
              fontWeight: 800,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}>
              MOD
            </span>
          </div>
        )}
        {/* Moderator counts toward the collapsed limit, but always show at
            least one real user (slice(0, 0) was hiding everyone) */}
        {orderedUsers.slice(0, usersExpanded ? undefined : Math.max(1, COLLAPSED_USER_COUNT - (moderatorCharacter ? 1 : 0))).map((user, i) => {
          const name = user.name || '?';
          const isLive = liveUserNames.has(name);
          const color = getUserColor(name);
          return (
            <div key={user.game_player_id || user.id || i} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '5px 8px',
              borderRadius: '8px',
              marginBottom: '2px',
              backgroundColor: isLive ? 'rgba(239,68,68,0.08)' : 'transparent',
              transition: 'background-color 0.2s',
            }}>
              {user.profile_image_url ? (
                <img src={user.profile_image_url} alt="" style={{
                  width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover',
                  border: isLive ? '2px solid #ef4444' : `1.5px solid ${color}`,
                  flexShrink: 0,
                }} />
              ) : (
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%',
                  backgroundColor: `${color}30`,
                  border: isLive ? '2px solid #ef4444' : `1.5px solid ${color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.55em', fontWeight: 700, color: color,
                  flexShrink: 0,
                }}>
                  {name.charAt(0).toUpperCase()}
                </div>
              )}
              <span style={{
                color: textColor,
                fontSize: '0.78em',
                fontWeight: 600,
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {name}
              </span>
              {isLive && (() => {
                const identity = liveIdentityByName.get(name);
                const isWatched = identity === watchedIdentity;
                return (
                  <>
                    {/* ▶ Watch — the stream picker: puts THIS streamer on the
                        stage (one streamer at a time) */}
                    {!isWatched && (
                      <button
                        onClick={() => onWatch && onWatch(identity)}
                        title={`Watch ${name}'s stream`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 3,
                          // Touch floor: ~14px tall on phones was the space's
                          // stream picker — give the tap something to hit
                          padding: IS_TOUCH ? '7px 14px' : '2px 8px',
                          minHeight: IS_TOUCH ? 30 : undefined,
                          borderRadius: 999,
                          border: '1px solid rgba(239,68,68,0.5)',
                          backgroundColor: 'rgba(239,68,68,0.15)',
                          color: '#ef4444',
                          fontSize: IS_TOUCH ? '0.68em' : '0.6em',
                          fontWeight: 800,
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          flexShrink: 0,
                          lineHeight: 1.4,
                        }}
                      >
                        ▶ Watch
                      </button>
                    )}
                    <span style={{
                      padding: '1px 5px',
                      borderRadius: '3px',
                      backgroundColor: isWatched ? '#ef4444' : 'rgba(239,68,68,0.25)',
                      color: isWatched ? '#fff' : '#ef4444',
                      fontSize: '0.6em',
                      fontWeight: 800,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      flexShrink: 0,
                      animation: isWatched ? 'liveBadgePulse 2s ease-in-out infinite' : 'none',
                    }}>
                      {isWatched ? '👁 Watching' : 'LIVE'}
                    </span>
                  </>
                );
              })()}
            </div>
          );
        })}
        {hasHiddenUsers && (
          <button
            onClick={() => setUsersExpanded(!usersExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              width: '100%',
              padding: '4px 8px',
              marginTop: '2px',
              background: 'none',
              border: 'none',
              color: mutedColor,
              fontSize: '0.7em',
              fontWeight: 600,
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'background-color 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {usersExpanded
              ? 'Show less'
              : `+${totalUserCount - COLLAPSED_USER_COUNT} more`
            }
            <span style={{
              display: 'inline-block',
              transform: usersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              fontSize: '0.9em',
            }}>▾</span>
          </button>
        )}
      </div>
    </>
  );
};

// 📺 Stream Eyes consent line (STREAM_EYES_SPEC_2026_08_05 §2.2, Nina's
// call): shown beside every go-live CTA — for camera AND screen — so nobody
// streams to the AI locals without knowing they're watching. Doubles as a
// feature ad on purpose.
const StreamEyesConsentNote = ({ color }) => (
  <p style={{ margin: 0, color, fontSize: '0.68em', fontWeight: 600, zIndex: 1, textAlign: 'center', opacity: 0.85 }}>
    👁 Heads up: the AIs in this space can see your stream
  </p>
);

const LivestreamPanel = ({ gameId, playerName, playerId, theme, onStatusChange, spaceUsers, moderatorCharacter, visible = true, onRequestShow, liveNow = false, liveStreamers = [], onPin, floatAnchorRef, watchRequest = null, isMobile = false, streamScene = null, forceDisconnect = false, publishRequest = null }) => {
  const THEME = theme || {};
  const [token, setToken] = useState(null);
  const [wsUrl, setWsUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [lkReady, setLkReady] = useState(liveKitLoaded);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishMode, setPublishMode] = useState(null); // 'camera' | 'screen' | null
  const [watchingDismissed, setWatchingDismissed] = useState(false); // viewer hit ✕ on a live stream — back to the pre-join stage (video hidden, audio unmounted) until they opt back in
  const [connectAttempts, setConnectAttempts] = useState(0); // failed/dropped connections since last stable connect — drives backoff
  const [wantsConnect, setWantsConnect] = useState(false); // user explicitly opted into the room (Go Live / Join / Watch) — the ONLY way a connection starts
  const [publishGranted, setPublishGranted] = useState(false); // token carries a canPublish grant (seat + secret verified server-side)
  const [pendingPublishMode, setPendingPublishMode] = useState(null); // 'camera' | 'screen' — CTA clicked while disconnected; auto-starts once in the room
  const [localWatchRequest, setLocalWatchRequest] = useState(null); // ▶ Watch clicked in the IDLE user list — connect, then watch that streamer (same shape as the watchRequest prop)
  const connectingRef = useRef(false);
  // Mirror of publishMode for callbacks that must not re-create on a mode
  // switch (announce heartbeat) and for the drop handler below, which runs
  // before React state settles.
  const publishModeRef = useRef(null);
  useEffect(() => { publishModeRef.current = publishMode; }, [publishMode]);

  const viewerIdentity = useMemo(() => String(playerId || getBrowserViewerId()).slice(0, 128), [playerId]);

  // A LiveKit connection is metered (connection minutes + bandwidth), and
  // click-to-join is UNIVERSAL (Nina 07-12): someone being live shows a join
  // poster — it never auto-connects, not even with the panel on screen. Every
  // path to a connection is an explicit opt-in (Join poster, ▶ Watch rows,
  // go-live chat message, stage takeover poster — all funnel through
  // wantsConnect / watchRequest).
  // 📱 Publisher exception (08-05): on phones the panel hides whenever the
  // user leaves the TV place, and a LiveKit drop there used to end the
  // broadcast silently — shouldConnect went false, so the auto-reconnect
  // bailed, the heartbeat died, and the MiniPlayer unmounted with zero
  // signal to the streamer. While a publish session is meant to be running
  // (publishing now, or a drop re-seeded pendingPublishMode — see
  // disconnect), stay connectable even while hidden. Mobile-gated: desktop
  // reconnect behavior is untouched.
  const shouldConnect = wantsConnect
    && (visible || (isMobile && (isPublishing || pendingPublishMode != null)));

  // A click on the go-live chat message ({identity, name, ts}) is an explicit
  // opt-in to enter the room — same standing as the Go Live path — covering
  // the window where the streamer's heartbeat hasn't flipped liveNow yet.
  useEffect(() => {
    if (!watchRequest) return;
    setWantsConnect(true);
    setWatchingDismissed(false);
  }, [watchRequest]);
  // 🧱 The room frame's composer "Share → Screen" (09-29, GameStepPage):
  // { mode: 'screen', ts } from outside the panel = the pre-join "Share
  // Screen" button's own start (connect, then the pending publish runs).
  // Absent = never fires. Same CAN_SCREEN_SHARE gate as that button.
  useEffect(() => {
    if (!publishRequest || publishRequest.mode !== 'screen' || !CAN_SCREEN_SHARE) return;
    setPendingPublishMode('screen');
    setWantsConnect(true);
  }, [publishRequest]);

  // Load LiveKit lazily — only once a connection is actually warranted, so an
  // idle open dock doesn't pull the heavy bundle for users who never stream
  useEffect(() => {
    if (!shouldConnect || liveKitLoaded) return;
    loadLiveKit().then(() => setLkReady(true)).catch(err => {
      console.error('Failed to load LiveKit:', err);
      setError('Failed to load streaming library');
    });
  }, [shouldConnect]);

  // Connect to LiveKit room. Seated players ask for a publish grant (verified
  // server-side against their player_secret); if that's denied — e.g. a stale
  // localStorage secret — fall back to a subscribe-only token so they can
  // still watch. Anonymous viewers always get subscribe-only.
  const connect = useCallback(async () => {
    if (connectingRef.current) return;
    connectingRef.current = true;
    setIsConnecting(true);
    setError(null);

    try {
      const base = `${API_BASE_URL}/api/takeGameStep?getLiveKitToken=true&roomName=${encodeURIComponent(gameId)}&participantName=${encodeURIComponent(playerName)}&participantIdentity=${encodeURIComponent(viewerIdentity)}`;
      const playerSecret = playerId ? chatGameApi.getPlayerSecret(playerId) : null;
      // This endpoint double-wraps the Lambda proxy envelope: the outer HTTP
      // status is 200 even for errors, with the REAL statusCode + body inside
      // the JSON — so denial checks must run on the parsed payload, never on
      // response.status (live-verified: publish-without-secret = HTTP 200
      // wrapping {statusCode: 403}).
      const fetchToken = async (url) => {
        // 12s cap — a hung backend otherwise leaves "Connecting..." up forever
        // with no way out (the auto-connect effect thinks a connect is in flight)
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 12000);
        let r;
        try {
          r = await fetch(url, { signal: ctrl.signal });
        } finally {
          clearTimeout(timer);
        }
        let d = null;
        try {
          d = await r.json();
          if (d && d.body && typeof d.body === 'string') d = JSON.parse(d.body);
        } catch (e) { /* non-JSON error body */ }
        return { httpOk: r.ok, httpStatus: r.status, data: d };
      };
      let result = null;
      if (playerId) {
        const publishUrl = `${base}&canPublish=true&playerId=${encodeURIComponent(playerId)}${playerSecret ? `&playerSecret=${encodeURIComponent(playerSecret)}` : ''}`;
        const pub = await fetchToken(publishUrl);
        // Any deny (real 403, wrapped 403, or just no token — e.g. stale
        // localStorage secret) falls back to subscribe-only so the user can
        // still watch instead of hard-failing the connect.
        if (pub.httpOk && pub.data && pub.data.token) result = pub;
      }
      if (!result) {
        result = await fetchToken(`${base}&canPublish=false`);
      }
      if (!result.httpOk || !result.data || !result.data.token) {
        throw new Error((result.data && result.data.error) || `HTTP ${result.httpStatus}`);
      }
      const data = result.data;
      console.log('[LivestreamPanel] Raw response:', JSON.stringify(data));
      setToken(data.token);
      setWsUrl(data.url);
      setPublishGranted(!!data.can_publish);
      setIsConnected(true);
      trackEvents.livestreamJoined({ room: gameId });
    } catch (err) {
      console.error('[LivestreamPanel] Connection error:', err);
      setError(err.name === 'AbortError' ? 'Connection timed out. Try again!' : (err.message || 'Failed to connect'));
      setConnectAttempts(a => a + 1);
    } finally {
      // Reset on success too — the token guard in the auto-connect effect is
      // what prevents duplicate connects; leaving this true after a success
      // blocked all future reconnects unless disconnect() happened to run.
      connectingRef.current = false;
      setIsConnecting(false);
    }
  }, [gameId, playerName, playerId, viewerIdentity]);

  // Auto-(re)connect whenever a connection is warranted and we're not in a
  // room. Runs again after any disconnect (token cleared) with exponential
  // backoff; after MAX_AUTO_RECONNECT consecutive failures only the manual
  // Retry button continues. onConnected resets the counter once stable.
  useEffect(() => {
    if (!lkReady || !shouldConnect || token || connectingRef.current) return;
    if (connectAttempts >= MAX_AUTO_RECONNECT) return;
    const delay = connectAttempts === 0 ? 0 : Math.min(1000 * 2 ** connectAttempts, 15000);
    const t = setTimeout(() => connect(), delay);
    return () => clearTimeout(t);
  }, [lkReady, shouldConnect, token, connectAttempts, connect]);

  // RoomContent (the only thing that reports status) is unmounted while
  // disconnected, so it can never report the zero state itself — without this
  // the dock header kept the last "N live" count and red glow forever after
  // a drop.
  useEffect(() => {
    if (isConnected || !onStatusChange) return;
    onStatusChange({ participantCount: 0, trackCount: 0, streamingParticipants: [], isConnected: false });
  }, [isConnected, onStatusChange]);

  const disconnect = useCallback(() => {
    trackEvents.livestreamLeft({ room: gameId });
    connectingRef.current = false;
    // 📱 Publisher resume (08-05): a drop mid-broadcast on a phone re-seeds
    // the pending mode, so the reconnect (kept alive by shouldConnect's
    // publisher exception) restarts the camera automatically — permission is
    // already granted, no gesture needed. A deliberate End stream clears
    // publishMode BEFORE any disconnect, so it never re-seeds. Camera only:
    // phones can't screen-share (CAN_SCREEN_SHARE).
    if (isMobile && publishModeRef.current === 'camera') {
      setPendingPublishMode('camera');
    }
    setToken(null);
    setWsUrl(null);
    setIsConnected(false);
    setIsPublishing(false);
    setPublishMode(null);
    // Count the drop so repeated instant disconnects (e.g. server rejecting the
    // room) back off instead of hot-looping through token mints.
    setConnectAttempts(a => a + 1);
  }, [gameId, isMobile]);

  // 🎛️ SIDEBAR COLLAPSE (Nina 09-15): hiding the room panel means a WATCHER
  // really leaves the metered LiveKit room — not a hidden meter behind a
  // collapsed rail. A PUBLISHER is exempt (publishing now, or a drop-reseeded
  // pending mode): a layout toggle must never end a broadcast silently — the
  // 08-05 phone lesson — and the MiniPlayer (gated on !visible) stays their
  // handle on the live session. wantsConnect drops too, so the auto-reconnect
  // doesn't rejoin behind the collapsed rail; reopening the panel shows the
  // normal click-to-join poster (connections are opt-in, never automatic).
  // Placed BELOW the disconnect useCallback on purpose — a dep array reading
  // it earlier is a TDZ crash at render (the 07-26 white-page class).
  useEffect(() => {
    if (!forceDisconnect) return;
    if (isPublishing || pendingPublishMode != null) return;
    if (!isConnected && !wantsConnect) return;
    setWantsConnect(false);
    if (isConnected) disconnect();
  }, [forceDisconnect, isPublishing, pendingPublishMode, isConnected, wantsConnect, disconnect]);

  // Announce publish state to the backend so clients WITHOUT the panel mounted
  // (collapsed bar, tab badges) still see who's live. LiveKit room state is only
  // visible to connected participants, hence this side channel. Heartbeats every
  // 45s while publishing; the backend expires entries after 90s without one.
  const announcePublishState = useCallback((state) => {
    // mode rides along so other clients know screen vs camera BEFORE they
    // connect — the screenshare stage-takeover poster keys off it. Read from
    // the ref, not state: a publishMode dep re-created this callback on a
    // camera→screen switch, and the heartbeat effect's cleanup then fired a
    // spurious 'stopped' — every other client saw the streamer blink offline.
    const url = `${API_BASE_URL}/api/takeGameStep?announceLivestream=true&roomName=${encodeURIComponent(gameId)}&participantIdentity=${encodeURIComponent(viewerIdentity)}&participantName=${encodeURIComponent(playerName || viewerIdentity)}&state=${state}&mode=${publishModeRef.current === 'screen' ? 'screen' : 'camera'}`;
    const attempt = () => fetch(url);
    return attempt().catch(err => {
      console.warn('[LivestreamPanel] announce failed:', err.message);
      // A lost 'stopped' means every client shows a ghost LIVE badge for the
      // full 90s heartbeat TTL — worth one retry. Lost 'live' self-heals on
      // the next 45s heartbeat.
      if (state === 'stopped') {
        setTimeout(() => attempt().catch(() => { /* TTL cleanup takes it from here */ }), 1500);
      }
    });
  }, [gameId, viewerIdentity, playerName]);

  useEffect(() => {
    if (!isPublishing) return;
    announcePublishState('live');
    const timer = setInterval(() => announcePublishState('live'), 45000);
    return () => {
      clearInterval(timer);
      announcePublishState('stopped');
    };
  }, [isPublishing, announcePublishState]);

  // Closing the tab / navigating away skips React cleanup, so the 'stopped'
  // announce above never fires and the space shows a ghost LIVE badge until
  // the backend's 90s heartbeat TTL. keepalive lets the request outlive the
  // page.
  useEffect(() => {
    if (!isPublishing) return;
    const announceStopOnLeave = () => {
      const url = `${API_BASE_URL}/api/takeGameStep?announceLivestream=true&roomName=${encodeURIComponent(gameId)}&participantIdentity=${encodeURIComponent(viewerIdentity)}&participantName=${encodeURIComponent(playerName || viewerIdentity)}&state=stopped`;
      try { fetch(url, { keepalive: true }); } catch (e) { /* page is going away */ }
    };
    window.addEventListener('pagehide', announceStopOnLeave);
    return () => window.removeEventListener('pagehide', announceStopOnLeave);
  }, [isPublishing, gameId, viewerIdentity, playerName]);

  const accentColor = THEME.accent || THEME.primary || '#7C4DFF';
  const bgColor = THEME.bgDark || THEME.bgMedium || '#1a1a2e';
  const textColor = THEME.textBright || THEME.textLight || '#fff';
  const mutedColor = THEME.textMuted || '#999';

  // Idle-view user list data — no LiveKit room yet, so who's live comes from
  // the status-poll announce side channel (liveStreamers = [{id, name}]).
  // The ids ARE LiveKit identities (announceLivestream sends viewerIdentity),
  // so a ▶ Watch here can seed the watch request the room honors on connect.
  const idleLiveNames = new Set((liveStreamers || []).map(s => s.name).filter(Boolean));
  const idleLiveIdentityByName = new Map((liveStreamers || []).filter(s => s.name && s.id != null).map(s => [s.name, String(s.id)]));

  // Not connected — idle stage until someone is live or the user opts in;
  // connecting state once a connection is warranted (see shouldConnect).
  // Styled as the SAME dark 16:9 stage tile the connected view renders, so
  // idle → connecting → live is one continuous surface instead of three
  // different-looking screens.
  if (!isConnected) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: bgColor,
        borderRadius: '8px',
        overflow: 'hidden',
      }}>
        <style>{`
          @keyframes livestreamBgPulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
          @keyframes livestreamSpin {
            to { transform: rotate(360deg); }
          }
          @keyframes liveBadgePulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridAutoRows: '1fr',
          gap: '6px',
          padding: '6px',
          minHeight: 140,
          overflow: 'hidden',
        }}>
          <div style={{ position: 'relative', minWidth: 0, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{
              position: 'relative',
              height: '100%',
              maxWidth: '100%',
              aspectRatio: '16 / 9',
              margin: 'auto',
              backgroundColor: 'rgba(0,0,0,0.45)',
              borderRadius: '8px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              padding: '16px',
              boxSizing: 'border-box',
            }}>
              {/* Animated gradient background — matches the connected placeholder */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: `
                  radial-gradient(ellipse at 20% 50%, ${accentColor}20 0%, transparent 50%),
                  radial-gradient(ellipse at 80% 20%, #6366f130 0%, transparent 50%),
                  radial-gradient(ellipse at 50% 80%, ${accentColor}15 0%, transparent 50%)
                `,
                animation: 'livestreamBgPulse 4s ease-in-out infinite',
              }} />
              {error ? (
                <>
                  <div style={{
                    color: '#ff6666',
                    fontSize: '0.8em',
                    padding: '6px 12px',
                    backgroundColor: 'rgba(255,100,100,0.12)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,100,100,0.3)',
                    zIndex: 1,
                    textAlign: 'center',
                  }}>
                    ⚠️ {error}
                  </div>
                  {/* Retry drops any stale pending publish — auto-firing a
                      camera prompt the user asked for minutes ago (before the
                      error) would be a surprise, not a resume */}
                  <button
                    onClick={() => { setConnectAttempts(0); setPendingPublishMode(null); setWantsConnect(true); connect(); }}
                    disabled={isConnecting}
                    style={{
                      padding: '8px 24px',
                      borderRadius: '999px',
                      border: 'none',
                      backgroundColor: isConnecting ? mutedColor : accentColor,
                      color: '#fff',
                      fontSize: '0.9em',
                      fontWeight: 700,
                      cursor: isConnecting ? 'wait' : 'pointer',
                      transition: 'background-color 0.2s',
                      boxShadow: `0 4px 16px ${accentColor}40`,
                      zIndex: 1,
                    }}
                  >
                    {isConnecting ? 'Connecting...' : 'Retry'}
                  </button>
                </>
              ) : shouldConnect ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                  <div style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    border: `2px solid ${accentColor}`,
                    borderTopColor: 'transparent',
                    animation: 'livestreamSpin 0.8s linear infinite',
                  }} />
                  <span style={{ color: textColor, fontSize: '0.9em', fontWeight: 600 }}>
                    {!lkReady
                      ? 'Loading...'
                      : pendingPublishMode
                        ? (pendingPublishMode === 'screen' ? 'Starting screen share...' : 'Starting camera...')
                        : 'Connecting to live room...'}
                  </span>
                </div>
              ) : (liveStreamers && liveStreamers.length > 0) ? (
                (() => {
                  // Someone IS live → join poster (click-to-join is universal;
                  // this replaces the old liveNow auto-connect). Screenshares
                  // lead over cameras — they're the stronger "show" signal.
                  const share = liveStreamers.find(s => s.mode === 'screen');
                  const lead = share || liveStreamers[0];
                  const joinStream = () => {
                    setLocalWatchRequest({ identity: lead.id, name: lead.name, ts: Date.now() });
                    setWatchingDismissed(false);
                    setWantsConnect(true);
                  };
                  return (
                    <>
                      <p style={{ margin: 0, color: textColor, fontSize: '0.9em', fontWeight: 700, zIndex: 1, textAlign: 'center' }}>
                        {share ? `🖥️ ${lead.name} is sharing their screen` : `🔴 ${lead.name} is live`}
                        {liveStreamers.length > 1 && (
                          <span style={{ color: mutedColor, fontWeight: 600 }}> · +{liveStreamers.length - 1} more</span>
                        )}
                      </p>
                      <button
                        onClick={joinStream}
                        style={{
                          padding: '10px 32px',
                          borderRadius: '999px',
                          border: '1px solid rgba(255,255,255,0.18)',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff',
                          fontSize: '0.85em',
                          fontWeight: 800,
                          letterSpacing: '0.3px',
                          cursor: 'pointer',
                          zIndex: 1,
                          transition: 'filter 0.15s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
                      >
                        ▶ Join stream
                      </button>
                      {playerId && (
                        <div style={{ display: 'flex', gap: '12px', zIndex: 1 }}>
                          {[{ label: 'Go Live', mode: 'camera' }, ...(CAN_SCREEN_SHARE ? [{ label: 'Share Screen', mode: 'screen' }] : [])].map(o => (
                            <button
                              key={o.mode}
                              onClick={() => { setPendingPublishMode(o.mode); setWantsConnect(true); }}
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                color: mutedColor, fontWeight: 700,
                                textDecoration: 'underline', textUnderlineOffset: '3px',
                                // Touch floor: the underlined-text look stays, the
                                // hit area doesn't shrink to the glyphs
                                fontSize: IS_TOUCH ? '0.8em' : '0.72em',
                                padding: IS_TOUCH ? '8px 12px' : 0,
                              }}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      )}
                      {playerId && <StreamEyesConsentNote color={mutedColor} />}
                    </>
                  );
                })()
              ) : playerId ? (
                <>
                  {/* Straight to the real CTAs — no "Go Live to see Go Live" gate.
                      The metered LiveKit connection is still deferred: it only
                      happens when a CTA is actually clicked, and the chosen mode
                      auto-starts once we're in the room (pendingPublishMode). */}
                  <p style={{ margin: 0, color: mutedColor, fontSize: '0.82em', fontWeight: 600, zIndex: 1, textAlign: 'center' }}>
                    Nobody's live yet — the stage is yours
                  </p>
                  {/* Basic buttons on purpose (user call): no emoji, no glow
                      animation (animated box-shadow = endless paint loop), no
                      hover transform, no backdrop blur — hovering these felt
                      heavy under all the decoration */}
                  <div style={{ display: 'flex', gap: '10px', width: '100%', maxWidth: 380, zIndex: 1 }}>
                <button
                  onClick={() => { setPendingPublishMode('camera'); setWantsConnect(true); }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff',
                    fontSize: '0.85em',
                    fontWeight: 800,
                    letterSpacing: '0.3px',
                    cursor: 'pointer',
                    transition: 'filter 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
                >
                  Go Live
                </button>
                {/* Hidden where getDisplayMedia doesn't exist (phones): the
                    old path connected first, then threw — a wasted metered
                    join and a lying "Try again!" for a permanent limitation */}
                {CAN_SCREEN_SHARE && (
                <button
                  onClick={() => { setPendingPublishMode('screen'); setWantsConnect(true); }}
                  style={{
                    flex: 1,
                    padding: '10px 16px',
                    borderRadius: '999px',
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: 'rgba(255,255,255,0.08)',
                    color: '#fff',
                    fontSize: '0.85em',
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                >
                  Share Screen
                </button>
                )}
              </div>
                  <StreamEyesConsentNote color={mutedColor} />
                </>
              ) : (
                <>
                  <p style={{ margin: 0, color: mutedColor, fontSize: '0.82em', fontWeight: 600, zIndex: 1, textAlign: 'center' }}>
                    Nobody's live right now.
                  </p>
                  <p style={{ margin: 0, color: mutedColor, fontSize: '0.8em', zIndex: 1 }}>
                    Join the game to go live!
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
        {/* The room's people, on the default view too — the panel should show
            the space is alive before anyone connects. ▶ Watch on a live row
            opts into the room (wantsConnect) and seeds the watch request the
            connected view honors once tracks exist. */}
        <SpaceUserList
          allUsers={spaceUsers || []}
          moderatorCharacter={moderatorCharacter}
          liveUserNames={idleLiveNames}
          liveIdentityByName={idleLiveIdentityByName}
          watchedIdentity={null}
          onWatch={(identity) => {
            setLocalWatchRequest({ identity, ts: Date.now() });
            setWatchingDismissed(false);
            setWantsConnect(true);
          }}
          textColor={textColor}
          mutedColor={mutedColor}
        />
      </div>
    );
  }

  // Connected — show LiveKit room
  if (!lkReady || !LiveKitComponents) {
    return <div style={{ padding: '20px', color: mutedColor, textAlign: 'center' }}>Loading streaming components...</div>;
  }

  const { LiveKitRoom, RoomAudioRenderer, VideoTrack, useTracks, useParticipants, useLocalParticipant } = LiveKitComponents;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      backgroundColor: bgColor,
      borderRadius: '8px',
      overflow: 'hidden',
    }}>
      {/* Room-level errors surfaced here too — the disconnected view's error box
          never renders once isConnected is true */}
      {error && (
        <div style={{
          padding: '5px 10px',
          backgroundColor: 'rgba(255,100,100,0.12)',
          borderBottom: '1px solid rgba(255,100,100,0.25)',
          color: '#ff8888',
          fontSize: '0.75em',
          flexShrink: 0,
        }}>
          ⚠️ {error}
        </div>
      )}
      <LiveKitRoom
        token={token}
        serverUrl={wsUrl}
        connect={true}
        onConnected={() => { setConnectAttempts(0); setError(null); }}
        onDisconnected={disconnect}
        onError={(err) => { console.error('[LiveKit] Room error:', err); setError(err.message); }}
        style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <RoomContent
          theme={THEME}
          accentColor={accentColor}
          textColor={textColor}
          mutedColor={mutedColor}
          onDisconnect={disconnect}
          isPublishing={isPublishing}
          setIsPublishing={setIsPublishing}
          publishMode={publishMode}
          setPublishMode={setPublishMode}
          onStatusChange={onStatusChange}
          spaceUsers={spaceUsers}
          moderatorCharacter={moderatorCharacter}
          visible={visible}
          onRequestShow={onRequestShow}
          watchingDismissed={watchingDismissed}
          setWatchingDismissed={setWatchingDismissed}
          publishGranted={publishGranted}
          onPin={onPin}
          floatAnchorRef={floatAnchorRef}
          pendingPublishMode={pendingPublishMode}
          onPendingHandled={() => setPendingPublishMode(null)}
          onError={setError}
          watchRequest={(localWatchRequest && (!watchRequest || localWatchRequest.ts >= watchRequest.ts)) ? localWatchRequest : watchRequest}
          gameId={gameId}
          playerId={playerId}
          streamScene={streamScene}
        />
        {/* Unmounting the audio renderer is what actually silences a dismissed
            stream — hiding the video grid alone would keep audio playing */}
        {!watchingDismissed && <RoomAudioRenderer />}
      </LiveKitRoom>
    </div>
  );
};

/**
 * Inner component rendered inside LiveKitRoom context
 */
const RoomContent = ({ theme, accentColor, textColor, mutedColor, onDisconnect, isPublishing, setIsPublishing, publishMode, setPublishMode, onStatusChange, spaceUsers, moderatorCharacter, visible = true, onRequestShow, watchingDismissed = false, setWatchingDismissed = () => {}, publishGranted = false, onPin, floatAnchorRef, pendingPublishMode = null, onPendingHandled = () => {}, onError = () => {}, watchRequest = null, gameId = null, playerId = null, streamScene = null }) => {
  const { useTracks, useParticipants, useLocalParticipant, useConnectionState, useRoomContext, Track, RoomEvent } = LiveKitComponents;
  const room = useRoomContext();
  const connectionState = useConnectionState();
  const isRoomConnected = connectionState === 'connected';
  const participants = useParticipants();
  const localParticipant = useLocalParticipant();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Report status to parent for header indicators. Keyed on a WHO signature,
  // not tracks.length: a same-length swap (A stops as B starts in one tick)
  // left stale streamingParticipants upstream.
  const trackSig = tracks.map(t => `${t.participant.sid}:${t.source}`).join('|');
  useEffect(() => {
    if (onStatusChange) {
      const streamingParticipants = tracks.map(t => ({
        name: t.participant.name || t.participant.identity,
        sid: t.participant.sid,
        source: t.source,
      }));
      onStatusChange({
        participantCount: participants.length,
        trackCount: tracks.length,
        streamingParticipants,
        isConnected: isRoomConnected,
      });
    }
  }, [participants.length, trackSig, isRoomConnected, onStatusChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // One streamer on stage at a time. With several people live the stage
  // showed every stream in a shrinking grid; now the viewer picks who to
  // watch (▶ buttons in the user list below) and the stage shows only that
  // streamer's tracks (camera + screen together if they publish both).
  const [watchedIdentity, setWatchedIdentity] = useState(null);
  // "Stream ended. Your camera is off!" reassurance on the idle stage after
  // the user ends their own broadcast — the moment they most need to KNOW the
  // camera is dark. Clears when they go live again.
  const [endedNotice, setEndedNotice] = useState(false);
  useEffect(() => {
    if (isPublishing) setEndedNotice(false);
  }, [isPublishing]);
  const streamerIdentities = useMemo(() => {
    const seen = [];
    tracks.forEach(t => {
      const id = t.participant.identity;
      if (!seen.includes(id)) seen.push(id);
    });
    return seen;
  }, [tracks]);
  // Fall back to the first streamer when nothing is picked yet or the picked
  // streamer ended
  const effWatchedIdentity = (watchedIdentity && streamerIdentities.includes(watchedIdentity))
    ? watchedIdentity
    : (streamerIdentities[0] || null);
  const stageTracks = useMemo(
    () => tracks.filter(t => t.participant.identity === effWatchedIdentity),
    [tracks, effWatchedIdentity]
  );
  // Watching one stream means HEARING one stream — silence streamers who are
  // live but not on stage (RoomAudioRenderer plays every subscribed track)
  useEffect(() => {
    tracks.forEach(t => {
      const p = t.participant;
      if (p.isLocal || typeof p.setVolume !== 'function') return;
      p.setVolume(p.identity === effWatchedIdentity ? 1 : 0);
    });
  }, [tracks, effWatchedIdentity]);

  // 📺 Stream Eyes sampler (STREAM_EYES_SPEC_2026_08_05 §3): while publishing,
  // grab ONE downscaled frame from the LOCAL track every 60s and POST it —
  // the backend describes it in text for the AI locals, then discards it (no
  // frame is ever stored). Publisher-side capture on purpose: no server video
  // infra, and this tab is guaranteed alive while live. Body is JSON+base64,
  // never multipart (APIGW corrupts multipart binary). Best-effort by
  // contract — nothing in here may ever disturb the broadcast.
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;
  useEffect(() => {
    if (!isPublishing || !gameId) return;
    let cancelled = false;
    const grabAndPost = async () => {
      let video = null;
      try {
        const locals = tracksRef.current.filter(t => t.participant?.isLocal && t.publication?.track?.mediaStreamTrack);
        // Both published (camera + screen)? The screen is what the room is
        // "watching" — describe that one.
        const local = locals.find(t => t.source === Track.Source.ScreenShare) || locals[0];
        const mst = local?.publication?.track?.mediaStreamTrack;
        if (!mst || mst.readyState !== 'live') return;
        video = document.createElement('video');
        video.muted = true;
        video.playsInline = true;
        video.srcObject = new MediaStream([mst]);
        await video.play().catch(() => {});
        await new Promise(r => setTimeout(r, 300)); // let a frame paint
        const w = video.videoWidth, h = video.videoHeight;
        if (!w || !h || cancelled) return;
        const outW = Math.min(640, w);
        const outH = Math.max(1, Math.round(h * (outW / w)));
        const canvas = document.createElement('canvas');
        canvas.width = outW;
        canvas.height = outH;
        canvas.getContext('2d').drawImage(video, 0, 0, outW, outH);
        const frameB64 = canvas.toDataURL('image/jpeg', 0.6).split(',')[1];
        if (!frameB64 || cancelled) return;
        await fetch(`${API_BASE_URL}/api/takeGameStep?streamFrame=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roomName: gameId,
            participantIdentity: localParticipant.localParticipant.identity,
            frame_b64: frameB64,
          }),
        });
      } catch (e) {
        // Sampling is a bonus — the stream itself must never notice a failure
      } finally {
        if (video) { try { video.pause(); video.srcObject = null; } catch (e) { /* detached */ } }
      }
    };
    // First frame shortly after go-live (the backend's 40s throttle paces
    // the actual describe calls regardless of what we send)
    const t0 = setTimeout(grabAndPost, 5000);
    const timer = setInterval(grabAndPost, 60000);
    return () => { cancelled = true; clearTimeout(t0); clearInterval(timer); };
  }, [isPublishing, gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCamera = useCallback(async () => {
    if (!isRoomConnected) { console.warn('Room not connected yet'); return; }
    try {
      setWatchingDismissed(false); // going live un-dismisses — your own tile must show
      await localParticipant.localParticipant.setCameraEnabled(true);
      await localParticipant.localParticipant.setMicrophoneEnabled(true);
      setIsPublishing(true);
      setPublishMode('camera');
      setWatchedIdentity(localParticipant.localParticipant.identity); // see your own preview
      onError(null);
      trackEvents.livestreamStarted({ type: 'camera' });
    } catch (err) {
      console.error('Failed to enable camera:', err);
      // NotReadable = another app holds the camera — the common phone case
      const name = err && err.name;
      onError(name === 'NotAllowedError'
        ? 'Camera is blocked. Check your browser permissions!'
        : name === 'NotFoundError'
          ? 'No camera found on this device.'
          : name === 'NotReadableError'
            ? 'Your camera is busy in another app. Close it and try again!'
            : 'Camera failed to start. Try again!');
    }
  }, [localParticipant, setIsPublishing, setPublishMode, isRoomConnected, setWatchingDismissed, onError]);

  const startScreenShare = useCallback(async () => {
    if (!isRoomConnected) { console.warn('Room not connected yet'); return; }
    // Belt-and-braces behind the hidden CTAs: no capture API, no attempt
    if (!CAN_SCREEN_SHARE) { onError('Screen sharing needs a desktop browser.'); return; }
    try {
      // selfBrowserSurface 'exclude': sharing the tab that displays the stream
      // creates an infinite mirror (recursive livestream tile trap) — keep the
      // current tab out of the share picker.
      setWatchingDismissed(false); // going live un-dismisses — your own tile must show
      await localParticipant.localParticipant.setScreenShareEnabled(true, { audio: true, selfBrowserSurface: 'exclude' });
      // Screen audio ≠ voice — turn the mic on too so screen-sharers can talk
      // (the stage mic toggle mutes it if unwanted)
      await localParticipant.localParticipant.setMicrophoneEnabled(true);
      setIsPublishing(true);
      setPublishMode('screen');
      setWatchedIdentity(localParticipant.localParticipant.identity); // see your own preview
      onError(null);
      trackEvents.livestreamStarted({ type: 'screen' });
    } catch (err) {
      console.error('Failed to share screen:', err);
      // NotAllowedError here is almost always the user closing the share
      // picker — not worth an error banner
      if (err && err.name === 'DeviceUnsupportedError') {
        onError('Screen sharing needs a desktop browser.');
      } else if (!(err && (err.name === 'NotAllowedError' || err.name === 'AbortError'))) {
        onError('Screen share failed to start. Try again!');
      }
    }
  }, [localParticipant, setIsPublishing, setPublishMode, isRoomConnected, setWatchingDismissed, onError]);

  // CTA was clicked back on the disconnected idle stage — start the chosen
  // mode as soon as we're actually in the room (and allowed to publish).
  // Screen share needs a recent user gesture for the browser's share picker;
  // a normal connect stays well inside that window, and if it doesn't the
  // catch inside startScreenShare just leaves the user on the pre-join stage
  // to click again (now instant — already connected).
  // The pending mode is only cleared AFTER the start attempt settles — the
  // camera permission prompt can sit open for seconds, and clearing early
  // flashed the Go Live CTAs back mid-flow (inviting double-clicks). The ref
  // guards against the effect re-firing while a start is in flight.
  const pendingRunRef = useRef(false);
  useEffect(() => {
    if (!pendingPublishMode || !isRoomConnected || pendingRunRef.current) return;
    if (!publishGranted) {
      // Token fell back to subscribe-only (stale seat secret) — say so
      // instead of silently eating the click
      onError("Couldn't verify your seat. Rejoin the game to go live!");
      onPendingHandled();
      return;
    }
    pendingRunRef.current = true;
    const run = pendingPublishMode === 'camera' ? startCamera() : startScreenShare();
    Promise.resolve(run).finally(() => {
      pendingRunRef.current = false;
      onPendingHandled();
    });
  }, [pendingPublishMode, isRoomConnected, publishGranted, startCamera, startScreenShare, onPendingHandled, onError]);

  const stopPublishing = useCallback(async () => {
    // Privacy-critical: this must ALWAYS end with the camera hardware off and
    // the live heartbeat stopped, even if LiveKit signaling throws midway.
    // The old sequential awaits could bail after disabling only the camera —
    // isPublishing stayed true, so the 45s heartbeat kept announcing LIVE
    // forever and badges never cleared.
    const lp = localParticipant.localParticipant;
    const results = await Promise.allSettled([
      lp.setCameraEnabled(false),
      lp.setMicrophoneEnabled(false),
      lp.setScreenShareEnabled(false),
    ]);
    // Hard fallback: stop any publication that survived. Stopping the
    // MediaStreamTrack releases the device (camera light OFF) regardless of
    // signaling state.
    let leftovers = 0;
    try {
      lp.trackPublications.forEach((pub) => {
        if (pub.track) {
          leftovers += 1;
          try { pub.track.mediaStreamTrack?.stop(); } catch (e) { /* already dead */ }
          try { lp.unpublishTrack(pub.track); } catch (e) { /* signaling down — device is off, which is what matters */ }
        }
      });
    } catch (e) { /* trackPublications unavailable — nothing left to force */ }
    // State flips UNCONDITIONALLY: the announce effect's cleanup fires
    // 'stopped' off this flip, killing the heartbeat and the LIVE badges.
    setIsPublishing(false);
    setPublishMode(null);
    setEndedNotice(true);
    trackEvents.livestreamStopped({});
    if (results.some(r => r.status === 'rejected') && leftovers > 0) {
      onError("Stream ended, but double-check your camera light. If it's still on, close this tab.");
    }
  }, [localParticipant, setIsPublishing, setPublishMode, onError]);

  const { VideoTrack: VideoTrackComponent } = LiveKitComponents;

  // 🛡️ Blur consent gate (MODERATION_P1): reveals persist per stream per
  // browser session, so re-renders and dock moves don't re-blur mid-watch.
  const [revealedStreams, setRevealedStreams] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('revealedStreams') || '[]')); } catch (e) { return new Set(); }
  });
  const revealStream = useCallback((key) => {
    setRevealedStreams(prev => {
      const next = new Set(prev);
      next.add(key);
      try { sessionStorage.setItem('revealedStreams', JSON.stringify([...next])); } catch (e) { /* private mode */ }
      return next;
    });
  }, []);

  // Emoji reactions — animated locally AND broadcast over the LiveKit data
  // channel so the streamer and every viewer see them (they were local-only
  // before: pure mirage). Lossy (unreliable) delivery is fine for reactions.
  const [floatingReactions, setFloatingReactions] = useState([]);
  const reactionEmojis = ['🔥', '❤️', '😂', '👏', '💀', '🎉', '😮', '💯'];
  const spawnReaction = useCallback((emoji) => {
    const id = Date.now() + Math.random();
    const left = 10 + Math.random() * 80; // random horizontal position %
    // Cap concurrent floats so a reaction flood can't melt the render loop
    setFloatingReactions(prev => (prev.length > 40 ? prev : [...prev, { id, emoji, left }]));
    setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => r.id !== id));
    }, 2000);
  }, []);
  const addReaction = useCallback((emoji) => {
    spawnReaction(emoji);
    try {
      const payload = new TextEncoder().encode(JSON.stringify({ t: 'reaction', e: emoji }));
      localParticipant.localParticipant.publishData(payload, { reliable: false });
    } catch (err) {
      console.warn('[LivestreamPanel] reaction send failed:', err.message);
    }
  }, [spawnReaction, localParticipant]);

  // Receive reactions from everyone else in the room
  useEffect(() => {
    if (!room || !RoomEvent) return;
    const onData = (payload) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg && msg.t === 'reaction' && typeof msg.e === 'string' && msg.e.length <= 8) {
          spawnReaction(msg.e);
        }
      } catch (err) { /* not a reaction payload */ }
    };
    room.on(RoomEvent.DataReceived, onData);
    return () => { room.off(RoomEvent.DataReceived, onData); };
  }, [room, RoomEvent, spawnReaction]);

  const hasStreams = tracks.length > 0;
  // Viewer hit ✕ — streams exist but this user opted back to the pre-join stage
  const showStreams = hasStreams && !watchingDismissed;

  // When the last stream ends, clear the dismissal so the next go-live shows
  useEffect(() => {
    if (!hasStreams && watchingDismissed) setWatchingDismissed(false);
  }, [hasStreams, watchingDismissed, setWatchingDismissed]);

  // Build set of names that are currently live-streaming (publishing a track)
  const liveUserNames = useMemo(() => {
    const names = new Set();
    tracks.forEach(t => {
      const name = t.participant.name || t.participant.identity;
      if (name) names.add(name);
    });
    return names;
  }, [tracks]);

  // name → LiveKit identity, for the ▶ Watch buttons in the user list
  const liveIdentityByName = useMemo(() => {
    const m = new Map();
    tracks.forEach(t => {
      const name = t.participant.name || t.participant.identity;
      if (name && !m.has(name)) m.set(name, t.participant.identity);
    });
    return m;
  }, [tracks]);

  // Watch requests from the go-live chat message. Each request ({identity,
  // name, ts}) is honored at most once (ts-keyed) but stays pending until
  // tracks exist — a click during connect still lands once the room is up.
  const handledWatchTsRef = useRef(null);
  useEffect(() => {
    if (!watchRequest || handledWatchTsRef.current === watchRequest.ts) return;
    if (streamerIdentities.length === 0) return;
    const wanted = (watchRequest.identity && streamerIdentities.includes(watchRequest.identity))
      ? watchRequest.identity
      : ((watchRequest.name && liveIdentityByName.get(watchRequest.name)) || streamerIdentities[0]);
    handledWatchTsRef.current = watchRequest.ts;
    setWatchedIdentity(wanted);
    setWatchingDismissed(false);
  }, [watchRequest, streamerIdentities, liveIdentityByName, setWatchingDismissed]);

  // Merge space users with LiveKit presence — space users are authoritative, LiveKit participants fill gaps
  const allUsers = useMemo(() => {
    if (spaceUsers && spaceUsers.length > 0) return spaceUsers;
    // Fallback: derive from LiveKit participants
    return participants.map(p => ({
      name: p.name || p.identity || '?',
      profile_image_url: null,
    }));
  }, [spaceUsers, participants]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
      {/* Global keyframes — always available */}
      <style>{`
        @keyframes livestreamBgPulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes reactionFloat {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          70% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-120px) scale(1.3); }
        }
        @keyframes livestreamSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes liveBadgePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', overflowY: 'auto' }}>

        {/* Video stage — ALWAYS rendered so expanding the panel drops straight
            into the live layout (Zoom pre-join style): when nobody is live, a
            placeholder tile keeps the exact same stage with the CTAs inside it.
            Every tile is a 16:9 stage that "contains" into its grid cell
            (height:100% + aspectRatio + maxWidth:100% — the browser transfers
            whichever constraint binds), so video always looks like video: no
            banner-shaped slots in a wide bar, no skyscrapers in a narrow tab. */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: stageTracks.length <= 1 ? '1fr' : stageTracks.length <= 4 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gridAutoRows: '1fr',
          gap: '6px',
          padding: '6px',
          minHeight: 140,
          overflow: 'hidden',
        }}>
          {/* ✕ leave — viewers only (publishers get End stream instead);
              returns to the pre-join stage without disconnecting */}
          {showStreams && !isPublishing && (
            <button
              onClick={() => setWatchingDismissed(true)}
              title="Stop watching"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 3,
                // Touch floor — 26px was under the repo's own 28px minimum
                width: IS_TOUCH ? 36 : 26,
                height: IS_TOUCH ? 36 : 26,
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.18)',
                // No backdrop blur on stage chrome: blur over playing video
                // re-blurs every frame and made the whole panel feel heavy
                backgroundColor: 'rgba(0,0,0,0.65)',
                color: '#fff',
                fontSize: '0.8em',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              ✕
            </button>
          )}
          {/* 👁 Stream Eyes transparency (spec §2.3, Nina's call): the latest
              scene description the AI locals were given, visible to humans on
              the stage. Fresh-only — a stale line under a live stream reads
              as the AIs being wrong. */}
          {(showStreams || isPublishing) && streamScene?.text && (Date.now() - streamScene.ts) < 5 * 60 * 1000 && (
            <div
              title="What the AI locals were just told is on this stream — they see text descriptions, never the video itself"
              style={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                zIndex: 3,
                maxWidth: '72%',
                padding: '4px 10px',
                borderRadius: 8,
                backgroundColor: 'rgba(0,0,0,0.6)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.68em',
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              what the AIs see: “{streamScene.text}”
            </div>
          )}
          {/* Publisher chrome lives ON the stage, not in the bottom bar — the
              bottom bar stays reactions-only so the buttons never crowd the
              emoji row. Top-left: live status + audience. Top-right: End. */}
          {isPublishing && (
            <>
              <span title="People connected to this stream" style={{
                position: 'absolute',
                top: 12,
                left: 12,
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                backgroundColor: 'rgba(239,68,68,0.92)',
                color: '#fff',
                fontSize: '0.72em',
                fontWeight: 800,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                boxShadow: '0 2px 10px rgba(239,68,68,0.4)',
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff',
                  animation: 'liveBadgePulse 2s ease-in-out infinite', display: 'inline-block',
                }} />
                Live
                <span style={{ opacity: 0.9, textTransform: 'none' }}>👁 {Math.max(0, participants.length - 1)}</span>
              </span>
              <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                {/* Quick mic toggle — works for camera AND screen share (both
                    publish the mic now); red = muted so the state is unmissable */}
                <button
                  onClick={() => localParticipant.localParticipant.setMicrophoneEnabled(!localParticipant.isMicrophoneEnabled)}
                  title={localParticipant.isMicrophoneEnabled ? 'Mute mic' : 'Unmute mic'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: IS_TOUCH ? 40 : 30,
                    height: IS_TOUCH ? 40 : 30,
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.22)',
                    background: localParticipant.isMicrophoneEnabled ? 'rgba(0,0,0,0.65)' : 'rgba(220,38,38,0.9)',
                    color: '#fff',
                    fontSize: '0.85em',
                    lineHeight: 1,
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'background 0.15s ease',
                  }}
                >
                  {localParticipant.isMicrophoneEnabled ? '🎙️' : '🔇'}
                </button>
                <button
                  onClick={stopPublishing}
                  title="Stop streaming"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '5px 12px',
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.22)',
                    background: 'rgba(0,0,0,0.65)',
                    color: '#fff',
                    fontSize: '0.75em',
                    fontWeight: 800,
                    letterSpacing: '0.3px',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                    height: IS_TOUCH ? 40 : 30,
                    boxSizing: 'border-box',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(220,38,38,0.9)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,0,0,0.65)'; }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#ef4444', display: 'inline-block' }} />
                  End stream
                </button>
              </div>
            </>
          )}
          {showStreams ? stageTracks.map((trackRef) => {
            // 🛡️ Blur consent gate (MODERATION_P1): remote human feeds start
            // blurred behind a click-to-reveal, once per stream per session.
            // Your own feed is never gated (you know what you're showing).
            const isLocalFeed = !!trackRef.participant.isLocal;
            const streamKey = `${trackRef.participant.identity}-${trackRef.source}`;
            const revealed = isLocalFeed || revealedStreams.has(streamKey);
            return (
            <div
              key={trackRef.participant.sid + '-' + trackRef.source}
              style={{ position: 'relative', minWidth: 0, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div style={{
                position: 'relative',
                height: '100%',
                maxWidth: '100%',
                aspectRatio: '16 / 9',
                margin: 'auto',
                backgroundColor: 'rgba(0,0,0,0.5)',
                borderRadius: '8px',
                overflow: 'hidden',
              }}>
                {/* Webcams crop-to-fill the 16:9 stage (like Zoom/Meet — most
                    cameras are 16:9 native so usually zero crop); screen shares
                    letterbox so content is never cropped */}
                <VideoTrackComponent
                  trackRef={trackRef}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: trackRef.source === 'screen_share' ? 'contain' : 'cover',
                    filter: revealed ? 'none' : 'blur(26px)',
                    transition: 'filter 0.25s ease',
                  }}
                />
                {!revealed && (
                  <div
                    onClick={() => revealStream(streamKey)}
                    style={{
                      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 10,
                      background: 'rgba(10,6,20,0.35)', cursor: 'pointer', textAlign: 'center', padding: 16,
                    }}
                  >
                    <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.95em' }}>🔴 Live from a real person</div>
                    <button style={{
                      border: 'none', borderRadius: 999, padding: '8px 20px', cursor: 'pointer',
                      background: '#fff', color: '#1a1128', fontWeight: 800, fontSize: '0.8em',
                    }}>
                      Tap to watch
                    </button>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.68em', maxWidth: 320 }}>
                      Live streams are unmoderated. If you see something bad, hit 🚩 and we're on it.
                    </div>
                  </div>
                )}
                <div style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '0.8em',
                    fontWeight: 600,
                  }}>
                    {trackRef.participant.name || trackRef.participant.identity}
                    {trackRef.source === 'screen_share' && ' (Screen)'}
                  </span>
                  {!isLocalFeed && revealed && (
                    <ReportFlag
                      gameId={gameId}
                      playerId={playerId}
                      kind="stream"
                      targetId={trackRef.participant.identity}
                      compact
                    />
                  )}
                </div>
              </div>
            </div>
            );
          }) : (
            <div style={{ position: 'relative', minWidth: 0, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* Placeholder tile — same 16:9 stage, blank until someone goes live */}
              <div style={{
                position: 'relative',
                height: '100%',
                maxWidth: '100%',
                aspectRatio: '16 / 9',
                margin: 'auto',
                backgroundColor: 'rgba(0,0,0,0.45)',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '16px',
                boxSizing: 'border-box',
              }}>
                {/* Animated gradient background */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: `
                    radial-gradient(ellipse at 20% 50%, ${accentColor}20 0%, transparent 50%),
                    radial-gradient(ellipse at 80% 20%, #6366f130 0%, transparent 50%),
                    radial-gradient(ellipse at 50% 80%, ${accentColor}15 0%, transparent 50%)
                  `,
                  animation: 'livestreamBgPulse 4s ease-in-out infinite',
                }} />
                {/* pendingPublishMode keeps the spinner up while the browser's
                    camera-permission prompt is open — without it the Go Live
                    CTAs flashed back mid-flow */}
                {(isPublishing || pendingPublishMode) ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                    <div style={{
                      width: '14px', height: '14px', borderRadius: '50%',
                      border: `2px solid ${accentColor}`,
                      borderTopColor: 'transparent',
                      animation: 'livestreamSpin 0.8s linear infinite',
                    }} />
                    <span style={{ color: textColor, fontSize: '0.9em', fontWeight: 600 }}>
                      Starting {(publishMode || pendingPublishMode) === 'screen' ? 'screen share' : 'camera'}...
                    </span>
                  </div>
                ) : (
                  <>
                    <div style={{ color: endedNotice ? '#4ade80' : mutedColor, fontSize: '0.82em', fontWeight: endedNotice ? 800 : 600, zIndex: 1, textAlign: 'center' }}>
                      {endedNotice
                        ? '✓ Stream ended. Your camera is off!'
                        : watchingDismissed ? 'You left the stream' : "Nobody's live yet — the stage is yours"}
                    </div>
                    {/* Way back in after ✕ — without this the stream is unreachable
                        until it ends */}
                    {hasStreams && watchingDismissed && (
                      <button
                        onClick={() => setWatchingDismissed(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 18px',
                          borderRadius: '999px',
                          border: '1px solid rgba(239,68,68,0.5)',
                          background: 'rgba(239,68,68,0.15)',
                          color: '#ef4444',
                          fontSize: '0.85em',
                          fontWeight: 800,
                          cursor: 'pointer',
                          zIndex: 1,
                        }}
                      >
                        ▶ {streamerIdentities.length} live — watch
                      </button>
                    )}
                    {/* Publish CTAs only render with a verified publish grant —
                        subscribe-only tokens would fail these at the server anyway */}
                    {!publishGranted && (
                      <p style={{ margin: 0, color: mutedColor, fontSize: '0.8em', zIndex: 1 }}>
                        Join the game to go live!
                      </p>
                    )}
                    {publishGranted && (
                    <div style={{ display: 'flex', gap: '10px', zIndex: 1, width: '100%', maxWidth: 380 }}>
                      {/* Basic buttons on purpose (user call): no emoji, no
                          glow animation, no hover transform, no backdrop blur
                          — hovering these felt heavy under the decoration */}
                      <button
                        onClick={startCamera}
                        disabled={!isRoomConnected}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          borderRadius: '999px',
                          border: '1px solid rgba(255,255,255,0.18)',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          color: '#fff',
                          fontSize: '0.85em',
                          fontWeight: 800,
                          letterSpacing: '0.3px',
                          cursor: isRoomConnected ? 'pointer' : 'not-allowed',
                          opacity: isRoomConnected ? 1 : 0.4,
                          transition: 'filter 0.15s ease',
                        }}
                        onMouseEnter={(e) => { if (isRoomConnected) e.currentTarget.style.filter = 'brightness(1.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.filter = ''; }}
                      >
                        Go Live
                      </button>
                      {/* Hidden where getDisplayMedia doesn't exist (phones) —
                          same gate as the disconnected stage */}
                      {CAN_SCREEN_SHARE && (
                      <button
                        onClick={startScreenShare}
                        disabled={!isRoomConnected}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          borderRadius: '999px',
                          border: '1px solid rgba(255,255,255,0.18)',
                          background: 'rgba(255,255,255,0.08)',
                          color: '#fff',
                          fontSize: '0.85em',
                          fontWeight: 700,
                          letterSpacing: '0.3px',
                          cursor: isRoomConnected ? 'pointer' : 'not-allowed',
                          opacity: isRoomConnected ? 1 : 0.4,
                          transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={(e) => { if (isRoomConnected) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      >
                        Share Screen
                      </button>
                      )}
                    </div>
                    )}
                    {publishGranted && <StreamEyesConsentNote color={mutedColor} />}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Floating emoji reactions */}
        {floatingReactions.map(r => (
          <div
            key={r.id}
            style={{
              position: 'absolute',
              bottom: '50px',
              left: `${r.left}%`,
              fontSize: '1.5em',
              pointerEvents: 'none',
              zIndex: 10,
              animation: 'reactionFloat 2s ease-out forwards',
            }}
          >
            {r.emoji}
          </div>
        ))}

      </div>

      {/* Bottom bar — reactions only, full width, for everyone. Publisher
          controls (viewer count, End stream) live on the stage overlay so
          nothing ever squeezes the emoji row. */}
      {(
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '6px 10px',
          backgroundColor: 'rgba(0,0,0,0.4)',
          flexShrink: 0,
        }}>
          {/* Reaction emoji bar — single horizontal row, scrolls if cramped */}
          <div style={{ display: 'flex', gap: '4px', flex: 1, flexWrap: 'nowrap', overflowX: 'auto', scrollbarWidth: 'none', minWidth: 0 }}>
            {reactionEmojis.map(emoji => (
              <button
                key={emoji}
                onClick={() => addReaction(emoji)}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '4px 8px',
                  fontSize: '1em',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                  flexShrink: 0,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* People strip lives UNDER the emoji row in the live view (Nina 07-12)
          and starts collapsed — the stage is the show once you're in a room */}
      <SpaceUserList
        allUsers={allUsers}
        moderatorCharacter={moderatorCharacter}
        liveUserNames={liveUserNames}
        liveIdentityByName={liveIdentityByName}
        watchedIdentity={watchingDismissed ? null : effWatchedIdentity}
        onWatch={(identity) => { setWatchedIdentity(identity); setWatchingDismissed(false); }}
        textColor={textColor}
        mutedColor={mutedColor}
        defaultExpanded={false}
      />

      {/* Floating mini-player — visible while a stream is active but the panel is
          hidden (other tab selected / bar collapsed). Portal escapes the
          display:none ancestor while keeping the LiveKitRoom context. Doubles as
          the streamer's "you're still live" cue. Draggable; snaps to corners. */}
      {/* Gated on !watchingDismissed — a dismissed stream must not chase the
          viewer around as a floating thumbnail. ✕ dismisses (same as the
          stage ✕) instead of disconnecting: staying in the room keeps rejoin
          instant, and wantsConnect would auto-reconnect a torn-down room
          anyway (the viewer already opted in this session). */}
      {!visible && hasStreams && !watchingDismissed && stageTracks[0] && (
        <MiniPlayer
          trackRef={isPublishing ? (tracks.find(t => t.participant.isLocal) || stageTracks[0]) : stageTracks[0]}
          VideoTrackComponent={VideoTrackComponent}
          isPublishing={isPublishing}
          onOpen={onRequestShow}
          onLeave={!isPublishing ? () => setWatchingDismissed(true) : undefined}
          onPin={onPin}
          floatAnchorRef={floatAnchorRef}
          reactions={floatingReactions}
          viewerCount={isPublishing ? Math.max(0, participants.length - 1) : null}
        />
      )}
    </div>
  );
};

/**
 * MiniPlayer — floating PiP thumbnail rendered into document.body.
 * Spawns just right of the chat column (the "dock" spot — close to where the
 * stream popped out from) when an anchor ref is provided, else bottom-right.
 * Drag anywhere; on release it snaps (animated) to the nearest of the four
 * corners or back to the dock spot, and remembers the choice (localStorage).
 * A sub-6px pointer gesture counts as a click and opens the full panel.
 * Viewers get an ✕ to leave the room; publishers don't (hiding a live
 * broadcast indicator would be unsafe — they stop from the panel). 📌 pins
 * the stream into the sidebar's pinned-tab slot (like pinning a tab).
 */
const MINI_W = 288; // default width — user-resizable 240..640, persisted (height always 16:9)
const MINI_MARGIN = 16;

const MiniPlayer = ({ trackRef, VideoTrackComponent, isPublishing, onOpen, onLeave, onPin, floatAnchorRef, reactions = [], viewerCount = null }) => {
  // 'tl' | 'tr' | 'bl' | 'br' | 'dock' — null until the user drags, so the
  // default can depend on whether a dock anchor exists at render time.
  // Key is v2: v1 (lk_mini_corner) predates the dock spot and would keep
  // everyone stuck on their old bottom-right choice. The v1 key is swept here
  // so it doesn't sit orphaned in localStorage forever.
  const [spot, setSpot] = useState(() => {
    try {
      localStorage.removeItem('lk_mini_corner');
      return localStorage.getItem('lk_mini_pos_v2') || null;
    } catch (e) { return null; }
  });
  const [miniW, setMiniW] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('lk_mini_w'), 10);
      return v >= 240 && v <= 640 ? v : MINI_W;
    } catch (e) { return MINI_W; }
  });
  const miniH = Math.round(miniW * 9 / 16);
  const resizeRef = useRef(null); // {startX, startW} while corner-resizing
  const [hovered, setHovered] = useState(false);
  // 📱 Touch devices never fire hover, so the Pin / Open-full / Leave-stream row
  // and the streamer-name scrim stayed at opacity 0 forever — Pin and Leave were
  // undiscoverable on a phone (07-26 audit). On a no-hover pointer, treat the
  // controls as always revealed rather than inventing a second tap-to-reveal
  // gesture that would fight the tile's own tap-to-open.
  const canHover = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: hover)').matches;
  const controlsShown = hovered || !canHover;
  const [, setViewportTick] = useState(0); // re-render on resize so corner coords stay pinned
  const elRef = useRef(null);
  const gestureRef = useRef(null);

  // Phone nav presence is STATE, not a render-time DOM read: the nav
  // unmounts while typing and remounts after, and a one-shot read could
  // leave the thumbnail at z 9999 — painted OVER the bottom nav (5100).
  // Checked on mount, on resize (keyboard show/hide fires it), and on a
  // cheap 2s tick; setState with an unchanged boolean skips the re-render.
  const [navBand, setNavBand] = useState(() => typeof document !== 'undefined'
    && !!document.querySelector('[data-swipe-nav]'));
  useEffect(() => {
    const check = () => setNavBand(!!document.querySelector('[data-swipe-nav]'));
    const onResize = () => { setViewportTick(t => t + 1); check(); };
    const tick = setInterval(check, 2000);
    window.addEventListener('resize', onResize);
    return () => { clearInterval(tick); window.removeEventListener('resize', onResize); };
  }, []);

  // 📏 Bottom band clearance (round 5, 08-03): on phones the fixed bottom
  // nav (+ comp phase ticker) owns the last ~53-79px of the viewport, and
  // innerHeight-based corner math parked the mini player ON it. Measure the
  // LIVE nav ([data-swipe-nav] — the arc's live-DOM pattern; the comp ticker
  // rides inside the same nav block so its rect includes it) instead of
  // hardcoding; no nav mounted (desktop) → innerHeight as before.
  const bottomLimit = () => {
    const nav = document.querySelector('[data-swipe-nav]');
    if (nav) {
      const r = nav.getBoundingClientRect();
      if (r.height > 0 && r.top > 100) return r.top;
    }
    return window.innerHeight;
  };
  const cornerCoords = (c) => ({
    tl: { x: MINI_MARGIN, y: MINI_MARGIN },
    tr: { x: window.innerWidth - miniW - MINI_MARGIN, y: MINI_MARGIN },
    bl: { x: MINI_MARGIN, y: bottomLimit() - miniH - MINI_MARGIN },
    br: { x: window.innerWidth - miniW - MINI_MARGIN, y: bottomLimit() - miniH - MINI_MARGIN },
  }[c] || { x: window.innerWidth - miniW - MINI_MARGIN, y: bottomLimit() - miniH - MINI_MARGIN });

  // The "dock" spot — top edge of the layout, just right of the chat column,
  // i.e. where the stream visually came from when it popped out. Recomputed
  // per render so sidebar resizes keep it glued to the chat edge.
  const dockCoords = () => {
    const rect = floatAnchorRef?.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return null;
    return {
      x: Math.max(4, Math.min(rect.right + 10, window.innerWidth - miniW - MINI_MARGIN)),
      y: Math.max(MINI_MARGIN, Math.min(rect.top, window.innerHeight - miniH - MINI_MARGIN)),
    };
  };
  // Until the user has ever dragged, prefer the dock spot when it exists
  const effSpot = spot || (dockCoords() ? 'dock' : 'br');

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const rect = elRef.current.getBoundingClientRect();
    gestureRef.current = { startX: e.clientX, startY: e.clientY, originX: rect.left, originY: rect.top, moved: false };
    elRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;
    if (!g.moved && Math.hypot(dx, dy) < 6) return;
    const el = elRef.current;
    if (!g.moved) {
      g.moved = true;
      // Direct-style drag: a setState per pointermove re-rendered the whole
      // thumbnail INCLUDING the live video track every frame — the "dragging
      // the live window feels slow" complaint. React never sees the drag; it
      // re-renders once, on release, when the snapped spot commits.
      el.style.transition = 'none';
      el.style.cursor = 'grabbing';
    }
    el.style.left = `${Math.min(Math.max(g.originX + dx, 4), window.innerWidth - miniW - 4)}px`;
    el.style.top = `${Math.min(Math.max(g.originY + dy, 4), bottomLimit() - miniH - 4)}px`;
  };
  const onPointerUp = (e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (!g) return;
    if (!g.moved) {
      if (onOpen) onOpen();
      return;
    }
    // Snap to whichever spot (four corners + dock, when available) the
    // thumbnail's center landed nearest
    const cx = g.originX + (e.clientX - g.startX) + miniW / 2;
    const cy = g.originY + (e.clientY - g.startY) + miniH / 2;
    const candidates = ['tl', 'tr', 'bl', 'br'].map(c => ({ c, p: cornerCoords(c) }));
    const d = dockCoords();
    if (d) candidates.push({ c: 'dock', p: d });
    let best = candidates[0];
    let bestDist = Infinity;
    candidates.forEach(cand => {
      const dist = Math.hypot(cx - (cand.p.x + miniW / 2), cy - (cand.p.y + miniH / 2));
      if (dist < bestDist) { bestDist = dist; best = cand; }
    });
    // Restore the transition, then write the snap target directly — when the
    // chosen spot is unchanged React skips the style patch, so the DOM move
    // can't be left to the re-render.
    const el = elRef.current;
    el.style.transition = 'left 0.3s cubic-bezier(0.2, 0.8, 0.3, 1), top 0.3s cubic-bezier(0.2, 0.8, 0.3, 1)';
    el.style.cursor = 'grab';
    el.style.left = `${best.p.x}px`;
    el.style.top = `${best.p.y}px`;
    setSpot(best.c);
    try { localStorage.setItem('lk_mini_pos_v2', best.c); } catch (err) { /* private mode */ }
  };

  const pos = effSpot === 'dock' ? (dockCoords() || cornerCoords('br')) : cornerCoords(effSpot);
  const name = trackRef.participant.name || trackRef.participant.identity;

  return createPortal(
    <div
      ref={elRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={isPublishing ? "You're live — click to open, drag to move" : 'Watching live — click to open, drag to move'}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: miniW,
        height: miniH,
        // Under the phone chrome band when a nav is mounted (dock 5000 /
        // nav 5100, above place layers 4600); desktop keeps its old
        // over-everything float (no band to respect there).
        zIndex: navBand ? 4800 : 9999,
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.14)',
        // Static shadow — hover-swapping a large blurred shadow repaints the
        // whole thumbnail region over live video (heavy-mouse complaint)
        boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.3)',
        backgroundColor: '#000',
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        // Drag/resize gestures flip these to 'none'/'grabbing' directly on the
        // DOM node (see onPointerMove) and restore them on release
        transition: 'left 0.3s cubic-bezier(0.2, 0.8, 0.3, 1), top 0.3s cubic-bezier(0.2, 0.8, 0.3, 1)',
        animation: 'lkMiniIn 0.25s ease',
      }}
    >
      <style>{`@keyframes lkMiniIn { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: none; } }`}</style>
      {/* Resize grip — drag to scale (16:9 preserved, 240..640px, persisted) */}
      <div
        onPointerDown={(e) => {
          e.stopPropagation();
          resizeRef.current = { startX: e.clientX, startW: miniW, lastW: miniW };
          elRef.current.style.transition = 'none';
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const r = resizeRef.current;
          if (!r) return;
          e.stopPropagation();
          // Dragging right grows when anchored left, shrinks when anchored
          // right (the dock spot hugs the chat column's right edge = left-anchored)
          const sign = (effSpot === 'dock' || effSpot.endsWith('l')) ? 1 : -1;
          const w = Math.max(240, Math.min(640, r.startW + sign * (e.clientX - r.startX)));
          r.lastW = w;
          // Direct-style resize — same reason as the drag: a setState per
          // pointermove re-rendered the live video every frame. Right/bottom
          // anchored spots keep their far edge pinned, matching what the old
          // per-frame re-render produced.
          const h = Math.round(w * 9 / 16);
          const el = elRef.current;
          el.style.width = `${w}px`;
          el.style.height = `${h}px`;
          if (effSpot === 'tr' || effSpot === 'br') el.style.left = `${window.innerWidth - w - MINI_MARGIN}px`;
          if (effSpot === 'bl' || effSpot === 'br') el.style.top = `${bottomLimit() - h - MINI_MARGIN}px`;
        }}
        onPointerUp={(e) => {
          const r = resizeRef.current;
          if (!r) return;
          e.stopPropagation();
          resizeRef.current = null;
          elRef.current.style.transition = 'left 0.3s cubic-bezier(0.2, 0.8, 0.3, 1), top 0.3s cubic-bezier(0.2, 0.8, 0.3, 1)';
          setMiniW(r.lastW);
          try { localStorage.setItem('lk_mini_w', String(r.lastW)); } catch (err) { /* private mode */ }
        }}
        title="Drag to resize"
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: 22,
          height: 22,
          zIndex: 4,
          cursor: 'nwse-resize',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          padding: 4,
          boxSizing: 'border-box',
          opacity: controlsShown ? 0.9 : 0,
          transition: 'opacity 0.15s',
          touchAction: 'none',
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10">
          <path d="M9 1 L1 9 M9 5 L5 9" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5" strokeLinecap="round" fill="none" />
        </svg>
      </div>
      <VideoTrackComponent
        trackRef={trackRef}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
      />

      {/* Top scrim — LIVE chip + hover controls */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 44,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        padding: 8,
        boxSizing: 'border-box',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.55), transparent)',
      }}>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          padding: '2px 8px',
          borderRadius: 6,
          backgroundColor: 'rgba(239,68,68,0.9)',
          color: '#fff',
          fontSize: '0.62em',
          fontWeight: 800,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff',
            animation: 'liveBadgePulse 2s ease-in-out infinite', display: 'inline-block',
          }} />
          {isPublishing ? "You're live" : 'Live'}
          {viewerCount != null && <span style={{ opacity: 0.9 }}>· 👁 {viewerCount}</span>}
        </span>
        <div style={{ display: 'flex', gap: 6, opacity: controlsShown ? 1 : 0, transition: 'opacity 0.15s' }}>
          {onPin && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onPin(); }}
              title="Pin to sidebar"
              style={miniBtnStyle}
            >📌</button>
          )}
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(); }}
            title="Open full view"
            style={miniBtnStyle}
          >⤢</button>
          {onLeave && (
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onLeave(); }}
              title="Leave stream"
              style={miniBtnStyle}
            >✕</button>
          )}
        </div>
      </div>

      {/* Incoming reactions float over the mini-player too — the streamer's
          main surface while browsing other tabs (reactionFloat keyframes come
          from RoomContent's <style>, which applies document-wide even inside
          a display:none ancestor) */}
      {reactions.map(r => (
        <div
          key={r.id}
          style={{
            position: 'absolute',
            bottom: 30,
            left: `${r.left}%`,
            fontSize: '1.2em',
            pointerEvents: 'none',
            zIndex: 2,
            animation: 'reactionFloat 2s ease-out forwards',
          }}
        >
          {r.emoji}
        </div>
      ))}

      {/* Bottom scrim — streamer name, hover only */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '18px 10px 8px',
        background: 'linear-gradient(0deg, rgba(0,0,0,0.6), transparent)',
        color: '#fff',
        fontSize: '0.75em',
        fontWeight: 600,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        opacity: controlsShown ? 1 : 0,
        transition: 'opacity 0.15s',
        pointerEvents: 'none',
      }}>
        {name}{trackRef.source === 'screen_share' && ' (Screen)'}
      </div>
    </div>,
    document.body
  );
};

const miniBtnStyle = {
  // Touch floor: 26px pins/close on a draggable thumbnail were misses
  width: IS_TOUCH ? 34 : 26,
  height: IS_TOUCH ? 34 : 26,
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.18)',
  backgroundColor: 'rgba(0,0,0,0.55)',
  color: '#fff',
  fontSize: '0.8em',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

export default LivestreamPanel;
